import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, CheckCircle2, Info, ExternalLink } from "lucide-react";
import { runSeoAudit, BAND_LABELS } from "@/lib/seoAudit";

const SEVERITY_ICON = {
  critical: AlertCircle,
  warn: AlertTriangle,
  ok: Info,
  pass: CheckCircle2,
};
const SEVERITY_COLOR = {
  critical: "text-red-600",
  warn: "text-amber-600",
  ok: "text-blue-600",
  pass: "text-emerald-600",
};

const BAND_FILTERS = [
  { key: "all", label: "All Pages" },
  { key: "critical", label: "Critical" },
  { key: "needs-work", label: "Needs Work" },
  { key: "good", label: "Good" },
  { key: "excellent", label: "Excellent" },
];

function ScoreDial({ score }) {
  let color = "text-emerald-600";
  if (score < 50) color = "text-red-600";
  else if (score < 70) color = "text-orange-600";
  else if (score < 90) color = "text-amber-600";
  return (
    <div className="text-center" data-testid="seo-overall-score">
      <div className={`text-6xl font-bold tabular-nums ${color}`}>{score}</div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mt-1">overall score</div>
    </div>
  );
}

function BandTile({ band, count, total }) {
  const meta = BAND_LABELS[band] || { label: band, color: "bg-gray-400" };
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="p-4 bg-white rounded border border-gray-200" data-testid={`seo-band-${band}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${meta.color}`} />
        <div className="text-[10px] uppercase tracking-widest text-gray-500">{meta.label}</div>
      </div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{count}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{pct}% of pages</div>
    </div>
  );
}

function PageRow({ page, expanded, onToggle }) {
  const meta = BAND_LABELS[page.band] || { label: page.band, color: "bg-gray-400" };
  return (
    <>
      <tr
        className="border-t border-gray-100 hover:bg-br-paper cursor-pointer"
        onClick={() => onToggle(page.path)}
        data-testid={`seo-page-${encodeURIComponent(page.path)}`}
      >
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${meta.color}`} />
            <span className="font-mono text-xs text-gray-800 truncate max-w-xs" title={page.path}>{page.path}</span>
            <Link
              to={page.path}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-br-bronze"
              target="_blank"
              rel="noreferrer"
              aria-label="Open page in new tab"
            >
              <ExternalLink size={12} />
            </Link>
          </div>
        </td>
        <td className="px-3 py-2 text-[10px] uppercase tracking-widest text-gray-500">{page.pageType}</td>
        <td className="px-3 py-2 text-sm font-bold tabular-nums text-right">{page.score}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{page.issues.length} issue{page.issues.length === 1 ? "" : "s"}</td>
      </tr>
      {expanded && (
        <tr className="bg-br-paper/40">
          <td colSpan={4} className="px-3 py-3">
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(page.checks).map(([k, c]) => {
                const Icon = SEVERITY_ICON[c.severity] || Info;
                return (
                  <div key={k} className="flex items-start gap-2 text-xs bg-white border border-gray-200 rounded p-2">
                    <Icon size={14} className={`${SEVERITY_COLOR[c.severity]} mt-0.5 shrink-0`} />
                    <div>
                      <div className="font-semibold uppercase tracking-widest text-[10px] text-gray-700">{k}</div>
                      <div className="text-gray-700">{c.message}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminSeoAudit() {
  const audit = useMemo(() => runSeoAudit(), []);
  const [bandFilter, setBandFilter] = useState("all");
  const [expanded, setExpanded] = useState(new Set());

  const handleToggle = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const visible = useMemo(() => {
    if (bandFilter === "all") return audit.pages;
    return audit.pages.filter((p) => p.band === bandFilter);
  }, [audit.pages, bandFilter]);

  return (
    <div className="space-y-5" data-testid="admin-seo-audit">
      <div className="grid lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-3 bg-white rounded border border-gray-200 p-5 flex items-center justify-center">
          <ScoreDial score={audit.overallScore} />
        </div>
        <div className="lg:col-span-9 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BandTile band="excellent" count={audit.byBand.excellent || 0} total={audit.totalPages} />
          <BandTile band="good" count={audit.byBand.good || 0} total={audit.totalPages} />
          <BandTile band="needs-work" count={audit.byBand["needs-work"] || 0} total={audit.totalPages} />
          <BandTile band="critical" count={audit.byBand.critical || 0} total={audit.totalPages} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded border border-gray-200">
          <div className="px-5 pt-4 pb-2 font-display tracking-widest text-sm uppercase text-gray-800">Pages By Type</div>
          <ul className="divide-y divide-gray-100">
            {audit.typeBreakdown.map((t) => (
              <li key={t.type} className="px-5 py-2 flex items-center justify-between text-sm" data-testid={`seo-type-${t.type}`}>
                <span className="uppercase tracking-widest text-xs text-gray-600">{t.type}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs">{t.count} pages</span>
                  <span className="font-bold tabular-nums text-gray-900">{t.avgScore}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded border border-gray-200">
          <div className="px-5 pt-4 pb-2 font-display tracking-widest text-sm uppercase text-gray-800">Top Issue Categories</div>
          {audit.topIssues.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-700">No issues across {audit.totalPages} pages. Nice.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {audit.topIssues.map((iss) => (
                <li key={iss.key} className="px-5 py-2 flex items-center justify-between text-sm">
                  <span className="uppercase tracking-widest text-xs text-gray-600">{iss.key}</span>
                  <span className="font-bold tabular-nums text-gray-900">{iss.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-200">
          <span className="text-[11px] tracking-[0.3em] text-gray-500">FILTER:</span>
          {BAND_FILTERS.map((opt) => {
            const isActive = bandFilter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setBandFilter(opt.key)}
                data-testid={`seo-filter-${opt.key}`}
                aria-pressed={isActive}
                className={`text-xs uppercase tracking-widest px-3 py-1 rounded border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-br-bronze ${
                  isActive
                    ? "bg-br-black text-white border-br-black"
                    : "bg-white text-gray-700 border-gray-300 hover:border-br-bronze"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-gray-500">
            <span className="font-bold text-gray-900">{visible.length}</span> of {audit.totalPages}
          </span>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-br-paper-2 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="px-3 py-2">Page</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2">Issues</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-sm text-gray-500">No pages in this band.</td></tr>
              ) : visible.map((p) => (
                <PageRow key={p.path} page={p} expanded={expanded.has(p.path)} onToggle={handleToggle} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
